import { ReduxSelectorProps } from '../../services/constants';
import { ComponentConfig } from '@measured/puck';
import { AxiosInstance } from 'axios';
import CustomTooltip from '../../../components/common/tooltip';
import TextCustomInput from '../../puck/reusable-props/TextCustomInput';
import { CustomizeProps } from '../../interface/commonInterface';
import {
  getBorderProps,
  getcurrentDeviceType,
  getMarginStyles,
  getPaddingStyles,
  getQuillEditorText,
  resolveShowComponentField,
} from '../../utils/helper';
import showComponent, {
  showComponentProps,
} from '../../puck/reusable-props/showComponent';
import { paddingFields, PaddingProps } from '../../puck/reusable-props/padding';
import { MarginProps, marginFields } from '../../puck/reusable-props/margin';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../puck/reusable-props/Border';
import { backgroungFields } from '../../puck/reusable-props/backgroungFields';

export type HeaderMenuProps = {
  header_1: CustomizeProps;
  header_2: CustomizeProps;
  border: IBorderValueProps;
  background: {
    color: string;
    isInherit: boolean;
  };
} & showComponentProps &
  PaddingProps &
  MarginProps;

export interface SimplePageComponentProps extends ReduxSelectorProps {
  baseService: AxiosInstance;
}
const defaultCommonProps = {
  weight: {
    mobile: 'font-normal',
    tablet: 'font-normal',
    desktop: 'font-normal',
  },
  showComponent: {
    mobile: true,
    tablet: true,
    desktop: true,
  },
  deviceType: 'desktop',
  textColor: {
    mobile: '#1b1818',
    tablet: '#1b1818',
    desktop: '#1b1818',
  },
  fontSize: {
    mobile: 'text-base',
    tablet: 'text-base',
    desktop: 'text-base',
  },
  hoverColor: {
    mobile: '#1b1818',
    tablet: '#1b1818',
    desktop: '#1b1818',
  },
};
export const HeaderMenu = ({
  useAppSelector,
}: SimplePageComponentProps): ComponentConfig<HeaderMenuProps> => {
  return {
    label: (
      <CustomTooltip title="SimplePageHeader">
        <span>Header Menu</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,
      ...backgroungFields,
      header_1: {
        type: 'custom',
        label: 'Header 1',
        render: ({ value, onChange, field }) => (
          <TextCustomInput
            isLink={true}
            showHover={true}
            value={value}
            onChange={onChange}
            title={field?.label}
          />
        ),
      },
      header_2: {
        type: 'custom',
        label: 'Header 2',
        render: ({ value, onChange, field }) => (
          <TextCustomInput
            isLink={true}
            showHover={true}
            value={value}
            onChange={onChange}
            title={field?.label}
          />
        ),
      },
      ...paddingFields,
      ...marginFields,
      border: {
        type: 'custom',
        label: 'Border',
        render: ({ value, onChange }) => (
          <Border
            value={value}
            onChange={value => onChange({ ...value })}
            title="Border"
          />
        ),
      },
    },
    defaultProps: {
      showComponent: ['desktop', 'tablet', 'mobile'],
      background: {
        color: '#ffffff',
        isInherit: false,
      },
      header_1: {
        linkValue: '',
        text: 'Cart',
        ...defaultCommonProps,
      },
      header_2: {
        linkValue: '',
        text: 'Checkout',
        ...defaultCommonProps,
      },
      padding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingTop: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
      },
      margin: {
        desktop: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        tablet: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        mobile: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
      },
      ...borderProps,
    },
    resolveFields: ({ props }, { fields }) =>
      resolveShowComponentField(props.id, fields),
    render: ({
      header_1,
      header_2,
      showComponent,
      padding,
      margin,
      editMode,
      background,
      border,
    }) => {
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const marginStyles = getMarginStyles(margin, currentDeviceType);
      const state = useAppSelector(state => state);
      const newHeader_1 = {
        ...header_1,
        text: getQuillEditorText(header_1?.text, state),
      };
      const newHeader2Text = {
        ...header_2,
        text: getQuillEditorText(header_2?.text, state),
      };
      return (
        <div
          className={`${border?.borderStyle?.[currentDeviceType]} ${showComponent?.includes(currentDeviceType) ? '' : '!hidden'}`}
          style={{
            ...marginStyles,
            backgroundColor: background?.isInherit
              ? 'inherit'
              : background?.color,
            ...getPaddingStyles(padding, currentDeviceType),
            ...getBorderProps(border, currentDeviceType),
          }}>
          <nav aria-label="breadcrumb">
            <ol className="flex items-center space-x-2 text-gray-500">
              <li>
                {newHeader_1.showComponent?.[currentDeviceType] && (
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={newHeader_1?.linkValue}
                    className={`${newHeader_1?.weight?.[currentDeviceType]} ${newHeader_1?.fontSize?.[currentDeviceType]}`}
                    style={{
                      color: newHeader_1?.textColor?.[currentDeviceType],
                    }}
                    onMouseOver={e => {
                      if (newHeader_1?.hoverColor) {
                        e.currentTarget.style.color =
                          newHeader_1?.hoverColor?.[currentDeviceType];
                      }
                    }}
                    onMouseOut={e => {
                      e.currentTarget.style.color =
                        newHeader_1?.textColor?.[currentDeviceType];
                    }}>
                    {newHeader_1?.text}
                  </a>
                )}
              </li>
              <li>
                <span className="text-gray-400">&gt;</span>
              </li>
              <li aria-current="page" className="text-gray-900">
                {newHeader2Text.showComponent?.[currentDeviceType] && (
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={newHeader2Text?.linkValue}
                    className={`${newHeader2Text?.weight?.[currentDeviceType]} ${newHeader2Text?.fontSize?.[currentDeviceType]}`}
                    style={{
                      color: newHeader2Text?.textColor?.[currentDeviceType],
                    }}
                    onMouseOver={e => {
                      if (newHeader2Text?.hoverColor) {
                        e.currentTarget.style.color =
                          newHeader2Text?.hoverColor?.[currentDeviceType];
                      }
                    }}
                    onMouseOut={e => {
                      e.currentTarget.style.color =
                        newHeader2Text?.textColor?.[currentDeviceType];
                    }}>
                    {newHeader2Text?.text}
                  </a>
                )}
              </li>
            </ol>
          </nav>
        </div>
      );
    },
  };
};
